import React, { useContext, useEffect, useState } from 'react';
import { Autocomplete, Box, Button, TextField } from '@mui/material';
import { DisableContext } from '../../contexts/DisableContextProvider';
import { UserSettingsCollapseKeys } from '../../types/User';
import { CountriesList } from '../../utility/countryData';
import { IANATimezoneList } from '../../utility/timezoneData';
import AccordionSection from '../AccordionSection';
import { EmailAccountLookup, EmailSendMethod, Organization, OrganizationSettings } from '../../types/organization';
import { useDebouncedOrganizationSettings } from '../../hooks/useDebounceSettings';
import { Loading } from '../../uikit';
import { inputContainerStyling, inputDividerStyling } from '../../styles/sxStyling';
import CustomAxios from '../../utility/customAxios';
import { Launch } from '@mui/icons-material';
import _ from 'lodash';

interface OrganizationSettingsFormProps {
  organization: Organization | null;
  setSaved: (saved: boolean) => void;
}

type SettingsOption<T> = {
  label: string;
  value: T;
};

const EMAIL_LOOKUP_OPTIONS: SettingsOption<EmailAccountLookup>[] = [
  {
    label: 'Always',
    value: 'always',
  },
  {
    label: 'Never',
    value: 'never',
  },
  {
    label: 'If no recipients in event',
    value: 'fallback',
  },
];

const EMAIL_SEND_OPTIONS: SettingsOption<EmailSendMethod>[] = [
  {
    label: 'Individual',
    value: 'individual',
  },
  {
    label: 'Group',
    value: 'group',
  },
];

const OrganizationSettingsForm: React.FC<OrganizationSettingsFormProps> = ({ organization, setSaved }) => {
  const { settings } = organization || {};
  const { disabled } = useContext(DisableContext);
  const { debouncedSet } = useDebouncedOrganizationSettings(500, {
    onSuccess: () => setSaved(true),
  });

  const [settingState, setSettingsState] = useState<OrganizationSettings>({
    country: '',
    time_zone: '',
    archive_offset: undefined,
    email: {
      account_lookup: 'never',
      send_method: 'individual',
    },
  });

  useEffect(() => {
    setSettingsState({
      country: settings?.country || '',
      time_zone: settings?.time_zone || '',
      archive_offset: settings?.archive_offset ?? undefined,
      email: {
        account_lookup: settings?.email?.account_lookup || 'never',
        send_method: settings?.email?.send_method || 'individual',
      },
    });
  }, [organization]);

  if (!organization) return <Loading />;

  const handleChange = (value: Partial<OrganizationSettings>) => {
    const next = _.merge(settingState, value);
    setSettingsState(next);
    debouncedSet(value);
  };

  const handleArchiveOffsetChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const raw = e.target.value;
    if (raw === '') {
      handleChange({ archive_offset: null }); // If cleared
      return;
    }
    const parsed = Number(raw);
    // Only allow values greater than or equal to 1
    if (!isNaN(parsed) && parsed >= 1) {
      handleChange({ archive_offset: parsed });
    }
  };

  const handleBilling = async () => {
    const response = await CustomAxios.get('/stripe/portal-session');
    window.location.href = response.data.url;
  };

  return (
    <AccordionSection title="Settings" collapseId={UserSettingsCollapseKeys.ORGANIZATION_ITEM_SETTINGS}>
      <Box sx={inputContainerStyling}>
        <Box sx={inputDividerStyling}>
          <Autocomplete
            fullWidth
            options={CountriesList}
            value={settingState.country}
            onChange={(_, country) => handleChange({ country: country || '' })}
            disabled={disabled}
            renderInput={(params) => <TextField {...params} label="Default Country" name="country" sx={{ minWidth: 220 }} />}
          />

          <Autocomplete
            fullWidth
            options={IANATimezoneList}
            value={settingState.time_zone}
            onChange={(_, time_zone) => handleChange({ time_zone: time_zone || '' })}
            disabled={disabled}
            renderInput={(params) => <TextField {...params} label="Default Time Zone" name="time_zone" sx={{ minWidth: 240 }} />}
          />

          <TextField
            fullWidth
            label="Archive Offset (days)"
            name="archive_offset"
            value={settingState.archive_offset?.toString() || ''}
            onChange={handleArchiveOffsetChange}
            disabled={disabled}
            slotProps={{
              htmlInput: { type: "number", min: 1 }
            }}
          />

          <Autocomplete
            fullWidth
            clearIcon={false}
            options={EMAIL_LOOKUP_OPTIONS}
            value={_.find(EMAIL_LOOKUP_OPTIONS, { value: settingState.email?.account_lookup })}
            onChange={(_, option) => handleChange({ email: { account_lookup: option?.value } })}
            disabled={disabled}
            renderInput={(params) => <TextField {...params} label="Lookup event email recipients in account" />}
          />

          <Autocomplete
            fullWidth
            clearIcon={false}
            options={EMAIL_SEND_OPTIONS}
            value={_.find(EMAIL_SEND_OPTIONS, { value: settingState.email?.send_method })}
            onChange={(_, option) => handleChange({ email: { send_method: option?.value } })}
            disabled={disabled}
            renderInput={(params) => <TextField {...params} label="How to address event emails" />}
          />
        </Box>
        <Box sx={inputDividerStyling}>
          <Button variant="outlined" onClick={handleBilling} endIcon={<Launch />}>
            Open billing
          </Button>
        </Box>
      </Box>

    </AccordionSection>
  );
};

export default OrganizationSettingsForm;
